// src/api/trades.js
import apiClient from './apiClient';

/**
 * Fetch closed trades for a symbol from the trade journal
 * @param {string} symbol - The trading symbol (e.g., 'BTC-USD')
 * @param {Object} options - Optional parameters
 * @param {string} options.status - Trade status filter (default: 'closed')
 * @param {boolean} options.includeNotes - Include trade notes (default: true for chart overlay)
 * @returns {Promise<Array>} Array of trades
 */
export const fetchTradesForSymbol = async (symbol, options = {}) => {
  const { status = 'closed', includeNotes = true } = options;
  const response = await apiClient.get('/trades', {
    params: {
      symbol,
      status,
      includeNotes,
    },
  });
  return response.data;
};

/**
 * Fetch notes for a specific trade
 * @param {string} tradeId - The trade ID
 * @returns {Promise<Array>} Array of notes with createdAt, text, linkedEventIds
 */
export const fetchNotesForTrade = async (tradeId) => {
  const response = await apiClient.get(`/trades/${tradeId}/notes`);
  return response.data?.notes || [];
};

/**
 * Confirm a planned trade — transitions status `planned` -> `open`.
 *
 * All body fields are optional. When omitted, the backend falls back to the
 * planned values already stored on the trade row. Used by the Review screen
 * to let the user override with actual fill data (e.g. real entry price).
 *
 * @param {string} tradeId
 * @param {Object} body
 * @param {number} [body.entryPrice]
 * @param {string} [body.entryAt] - ISO timestamp
 * @param {number} [body.positionSize]
 * @returns {Promise<Object>} The updated trade
 */
export const confirmTrade = async (tradeId, body = {}) => {
  const response = await apiClient.post(`/trades/${tradeId}/confirm`, body);
  return response.data?.trade || response.data;
};

/**
 * Skip a planned trade — transitions status `planned` -> `skipped`.
 *
 * `skipReasons` is a multi-select array (at least one), each one of the
 * canonical enum values:
 *   - entry_extended_from_anchor
 *   - risk_reward_poor
 *   - thesis_already_played_out
 *   - low_conviction_signal
 *   - regime_risk_off
 *   - account_drawdown_pause
 *   - broker_access
 *   - other
 * A legacy single `skipReason` string is still accepted by the backend.
 *
 * `skipNote` is optional, but required when `other` is among the reasons.
 *
 * @param {string} tradeId
 * @param {Object} body
 * @param {string[]} [body.skipReasons]
 * @param {string} [body.skipReason] legacy single value
 * @param {string} [body.skipNote]
 * @returns {Promise<Object>} The updated trade
 */
export const skipTrade = async (tradeId, body) => {
  const response = await apiClient.post(`/trades/${tradeId}/skip`, body);
  return response.data?.trade || response.data;
};

/**
 * Fetch the counterfactual scorecard for skipped trades — what would have
 * happened had each skipped signal been taken. Each outcome replays the
 * decision-time-frozen entry/SL/TP against daily candles (stop-first /
 * target-first / running). Returns { outcomes, aggregates, meta } where
 * aggregates carries per-reason + per-strategy rollups and the cumulative
 * saved-R series.
 */
export const fetchDecisionStats = async () => {
  const response = await apiClient.get('/trades/decision-stats');
  return response.data;
};

export const fetchSkipOutcomes = async () => {
  const response = await apiClient.get('/trades/skip-outcomes');
  return response.data;
};

export default {
  fetchTradesForSymbol,
  fetchNotesForTrade,
  confirmTrade,
  skipTrade,
  fetchSkipOutcomes,
  fetchDecisionStats,
};
